import { _dispositivoTypeUtilsData } from "./dispositivoTypeUtilsData";
import {
  BitmaskMappaturaAllarmi,
  NodoDto,
  DispositivoType,
  EqualityMappaturaAllarmi,
  NodoTipo,
} from "@/api";
import { ReactNode } from "react";
import WarningIcon from "@/assets/warning.png";
import ErrorIcon from "@/assets/error.png";
import OkIcon from "@/assets/ok.png";

export const DispositivoRefetchInterval = 15_000;

export interface DispositivoColor {
  color: string;
  icon: ReactNode | null;
}

export function getDispositivoColor(
  dispositivo: NodoDto,
  options: {
    sourceLastColor?: boolean;
    sourceNovusDiagnostic?: boolean;
  }
): DispositivoColor | null {
  if (
    options.sourceLastColor &&
    typeof dispositivo.dispositivoLastColor === "number"
  ) {
    const lastColor = dispositivo.dispositivoLastColor;

    switch (lastColor) {
      case 1:
        return {
          color: "#4CAF50",
          icon: <img src={OkIcon} width={20} height={20} alt="Verde" />,
        };
      case 2:
        return {
          color: "#1E90FF",
          icon: null,
        };
      case 3:
        return {
          color: "#FFA500",
          icon: <img src={WarningIcon} width={20} height={20} alt="Giallo" />,
        };
      case 4:
        return {
          color: "#F44336",
          icon: <img src={ErrorIcon} width={20} height={20} alt="Rosso" />,
        };
      default:
        return null;
    }
  }

  if (options.sourceNovusDiagnostic) {
    const value = dispositivo.dispositivoProprietaMisurate?.find(
      (x) => x.type?.influxDbTag === "novaDiagnostic"
    );
    if (typeof value?.lastValue?.value === "number") {
      // documentazione Vibratio 35
      switch (value?.lastValue.value) {
        case 1:
          // verde
          return {
            color: "#00bf63",
            icon: <img src={OkIcon} width={20} height={20} alt="Verde" />,
          };
        case 2:
          // giallo
          return {
            color: "#ff914d",
            icon: <img src={WarningIcon} width={20} height={20} alt="Giallo" />,
          };
        case 3:
          // arancio
          return {
            color: "#ffde59",
            icon: null,
          };
        case 4:
          // rosso
          return {
            color: "#ff3131",
            icon: <img src={ErrorIcon} width={20} height={20} alt="Rosso" />,
          };
        default:
          return null;
      }
    }
  }

  return null;
}

export interface DispositivoTypeHelper {
  label: string;
}

const dispositivoTypesData: {
  [type in DispositivoType]: (typeof _dispositivoTypeUtilsData)[keyof typeof _dispositivoTypeUtilsData];
} = {
  unknown: _dispositivoTypeUtilsData.Unknown,
  macchinario: _dispositivoTypeUtilsData.Macchinario,
  sensore: _dispositivoTypeUtilsData.Sensore,
} as const;

export interface DispositivoTypeOption {
  value: DispositivoType;
  label: string;
}

export function getDispositivoTypes(
  language?: string
): DispositivoTypeOption[] {
  const lang = (language || "it") as "it" | "de" | "en";
  return Object.entries(dispositivoTypesData).map(([value, data]) => ({
    value: value as DispositivoType,
    label: data.label[lang] || data.label.it,
  }));
}

export function getDispositivoTypeHelper(
  type: DispositivoType,
  language?: string
): DispositivoTypeHelper | null {
  // if (type === "unknown") {
  //   return null;
  // }
  const data = dispositivoTypesData[type];
  if (!data) {
    console.error("[DispositivoTypeHelper] Unknown type: " + type);
    return null;
  }

  const lang = (language || "it") as "it" | "de" | "en";
  const label = data.label[lang] || data.label.it;

  return {
    label,
  };
}

export type MacchinarioStatus = "off" | "stop" | "on" | "on-manuale" | "alarm";
export type SensoreStatus = "off" | "on" | "alarm";
export type DispositivoStatus = MacchinarioStatus | SensoreStatus;

export const TipoDispositivoByTipoNodo: {
  [tipoDispositivo in NodoTipo]: DispositivoType;
} = {
  Azienda: "unknown",
  Contenitore: "unknown",
  Dispositivo_Macchinario: "macchinario",
  Dispositivo_Sensore: "sensore",
  Gateway: "unknown",
  Ripetitore: "unknown",
};

export const TipoNodoByTipoDispositivo: {
  [tipoDispositivo in DispositivoType]: NodoTipo;
} = {
  macchinario: "Dispositivo_Macchinario",
  sensore: "Dispositivo_Sensore",
  unknown: "Contenitore",
};

// -------------------------------------------------
// -------------------------------------------------
// -------------------------------------------------

export function getDispositivoTypeLabel(
  type: DispositivoType,
  language?: string
): string {
  return getDispositivoTypeHelper(type, language)!.label;
}

// -------------------------------------------------
// -------------------------------------------------
// -------------------------------------------------

export function getAllarmiDispositivo(dispositivo: NodoDto): string[] | null {
  const modello = dispositivo.dispositivoModello!;
  if (!modello) {
    return null;
  }

  const hasAlarmStatus = dispositivo.dispositivoLastStatus === "alarm";
  const hasAlarmCode =
    typeof dispositivo.dispositivoLastAlarmStatus === "string" &&
    dispositivo.dispositivoLastAlarmStatus !== "" &&
    dispositivo.dispositivoLastAlarmStatus !== "0";

  if (hasAlarmCode) {
    if (!modello) {
      // non conosciamo questo modello, facciamo fallback sul codice inviato dal dispositivo
      return [dispositivo.dispositivoLastAlarmStatus!];
    }

    const allarmi = modello.mappaturaAllarmi;

    if (allarmi?.type === "Bitmask") {
      const config = allarmi as BitmaskMappaturaAllarmi;

      try {
        const statusNumber = BigInt(dispositivo.dispositivoLastAlarmStatus!);

        const messages: string[] = [];
        for (const bit of config.bits ?? []) {
          if (typeof bit.index === "number" && bit.message) {
            const isBitSet =
              (statusNumber & (BigInt(1) << BigInt(bit.index))) !== BigInt(0);
            if (isBitSet) {
              messages.push(bit.message);
            }
          }
        }

        return messages;
      } catch (e) {
        // fallback
        return [dispositivo.dispositivoLastAlarmStatus!];
      }
    } else if (allarmi?.type === "Equality") {
      const config = allarmi as EqualityMappaturaAllarmi;
      const message = config.mappings?.find(
        (m) => m.value === dispositivo.dispositivoLastAlarmStatus
      )?.message;
      return message ? [message] : [];
    } else {
      // non conosciamo questa mappatura, facciamo fallback sul codice inviato dal dispositivo
      return [dispositivo.dispositivoLastAlarmStatus!];
    }
  } else {
    if (hasAlarmStatus) {
      // siamo in stato di allarme, ma non c'è un alarm code
      return ["Allarme sconosciuto"];
    } else {
      // nessun allarme
      return [];
    }
  }
}
